import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ScheduledReportSftpSecretType } from '../../../services/scheduled-reports/scheduled-reports.service';

export interface PayrollReportSftpSecretDialogData {
  type: ScheduledReportSftpSecretType;
  prefix: string;
}

export interface PayrollReportSftpSecretDialogResult {
  name: string;
  value: string;
}

@Component({
    selector: 'app-payroll-report-sftp-secret-dialog',
    templateUrl: './payroll-report-sftp-secret-dialog.component.html',
    styleUrls: ['./payroll-report-sftp-secret-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PayrollReportSftpSecretDialogComponent {
  private readonly secretNamePattern = /^[A-Za-z0-9+=.@_-]+$/;
  protected readonly form: FormGroup;

  constructor(
    formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<PayrollReportSftpSecretDialogComponent, PayrollReportSftpSecretDialogResult>,
    @Inject(MAT_DIALOG_DATA) protected data: PayrollReportSftpSecretDialogData,
  ) {
    this.form = formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(128), Validators.pattern(this.secretNamePattern)]],
      value: ['', Validators.required],
    });
  }

  protected get title(): string {
    return `Add ${this.typeLabel} secret`;
  }

  protected get typeLabel(): string {
    switch (this.data.type) {
      case 'password':
        return 'SFTP password';
      case 'private_key':
        return 'SFTP private key';
      case 'pgp_public_key':
        return 'PGP public key';
    }
  }

  protected get valueLabel(): string {
    return this.data.type === 'password' ? 'Password' : this.typeLabel;
  }

  protected get validationMessages(): string[] {
    const messages: string[] = [];
    const nameControl = this.form.get('name');
    const valueControl = this.form.get('value');

    if (nameControl?.hasError('required')) {
      messages.push('Secret name is required.');
    } else if (nameControl?.hasError('maxlength')) {
      messages.push('Secret name cannot exceed 128 characters.');
    } else if (nameControl?.hasError('pattern')) {
      messages.push('Secret name can only contain letters, numbers, +, =, ., @, _, or -.');
    }

    if (valueControl?.hasError('required')) {
      messages.push(`${this.valueLabel} is required.`);
    }

    return messages;
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  protected submit(): void {
    const raw = this.form.getRawValue();
    const name = `${raw.name || ''}`.trim();
    this.form.get('name')?.setValue(name);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      name,
      value: `${raw.value || ''}`,
    });
  }
}
